import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const easeOut = [0.19, 1, 0.22, 1];

/**
 * HeroSlider — fetches active hero banners from /api/hero-banners and
 * fades between them. Desktop and mobile use their respective images.
 * If no banners are active, returns null so the caller can render a
 * fallback.
 */
export default function HeroSlider({ intervalMs = 6000 }) {
  const [banners, setBanners] = useState([]);
  const [i, setI] = useState(0);
  const desktopVideoRef = useRef(null);
  const mobileVideoRef = useRef(null);

  useEffect(() => {
    api.get("/hero-banners").then(({ data }) => setBanners(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (banners.length < 2) return;
    const t = setInterval(() => setI((v) => (v + 1) % banners.length), intervalMs);
    return () => clearInterval(t);
  }, [banners.length, intervalMs]);

  // Force autoplay for both video refs on every slide change (some browsers
  // block autoplay unless muted is explicitly set on the element property).
  useEffect(() => {
    for (const ref of [desktopVideoRef, mobileVideoRef]) {
      const el = ref.current;
      if (!el) continue;
      el.muted = true;
      el.playsInline = true;
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [i, banners.length]);

  if (!banners.length) return null;
  const b = banners[i] || banners[0];

  return (
    <section className="relative overflow-hidden" data-testid="hero-slider">
      <div className="relative h-screen min-h-[600px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={b.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: easeOut }}
            className="absolute inset-0"
          >
            {b.desktop_video || b.mobile_video ? (
              <>
                {/* Mobile video (uses mobile_video if present, else desktop) */}
                <video
                  ref={mobileVideoRef}
                  className="absolute inset-0 w-full h-full object-cover md:hidden"
                  src={b.mobile_video || b.desktop_video}
                  poster={b.mobile_image || b.desktop_image || undefined}
                  autoPlay
                  muted
                  defaultMuted
                  loop
                  playsInline
                  preload="metadata"
                />
                <video
                  ref={desktopVideoRef}
                  className="absolute inset-0 w-full h-full object-cover hidden md:block"
                  src={b.desktop_video}
                  poster={b.desktop_image || undefined}
                  autoPlay
                  muted
                  defaultMuted
                  loop
                  playsInline
                  preload="metadata"
                />
              </>
            ) : (
              <>
                <img
                  src={b.mobile_image || b.desktop_image}
                  alt={b.title || "Hero"}
                  className="absolute inset-0 w-full h-full object-cover md:hidden"
                />
                <img
                  src={b.desktop_image}
                  alt={b.title || "Hero"}
                  className="absolute inset-0 w-full h-full object-cover hidden md:block"
                />
              </>
            )}
            {/* Overlay for legibility */}
            <div className="absolute inset-0 bg-black/30" />
          </motion.div>
        </AnimatePresence>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-16 h-full flex items-end lg:items-center pb-16 lg:pb-0">
          <motion.div
            key={`copy-${b.id}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: easeOut, delay: 0.1 }}
            className="max-w-2xl text-white"
          >
            {b.eyebrow ? (
              <p className="label-xs text-white/80 tracking-[0.4em]">{b.eyebrow}</p>
            ) : null}
            {b.title ? (
              <h1 className="mt-5 text-5xl sm:text-6xl lg:text-[6rem] leading-[0.95] tracking-tighter font-serif">
                {b.title}
              </h1>
            ) : null}
            {b.subtitle ? (
              <p className="mt-6 max-w-md text-base leading-relaxed text-white/85">{b.subtitle}</p>
            ) : null}
            {b.cta_label && b.cta_link ? (
              <div className="mt-10">
                {b.cta_link.startsWith("/") ? (
                  <Link to={b.cta_link}>
                    <Button
                      data-testid="hero-banner-cta"
                      className="rounded-none px-10 py-6 label-xs bg-white text-black hover:bg-white/90"
                    >
                      {b.cta_label}
                    </Button>
                  </Link>
                ) : (
                  <a href={b.cta_link} target="_blank" rel="noreferrer">
                    <Button className="rounded-none px-10 py-6 label-xs bg-white text-black hover:bg-white/90">
                      {b.cta_label}
                    </Button>
                  </a>
                )}
              </div>
            ) : null}
          </motion.div>
        </div>

        {banners.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {banners.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setI(idx)}
                aria-label={`Go to slide ${idx + 1}`}
                data-testid={`hero-dot-${idx}`}
                className={`h-1 transition-all ${
                  idx === i ? "w-8 bg-white" : "w-4 bg-white/40 hover:bg-white/70"
                }`}
                style={{ transitionProperty: "width, background-color" }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
